import React, { useState } from "react";
import { Fab, Tooltip, Badge, useTheme, useMediaQuery } from "@mui/material";
import { WhatsApp } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

/**
 * WhatsApp Floating Action Button
 *
 * Persistent button in bottom-right corner for WhatsApp Business integration.
 * Features:
 * - Click-to-chat functionality
 * - Smooth animations
 * - Badge for notifications
 * - Hover effects
 * - Mobile responsive positioning
 */

interface WhatsAppFabProps {
  phoneNumber?: string; // WhatsApp Business number (without + or country code formatting)
  message?: string; // Pre-filled message
  show?: boolean; // Control visibility
  badgeContent?: number; // Optional badge number
}

const WhatsAppFab: React.FC<WhatsAppFabProps> = ({
  phoneNumber = "919404880316", // Default to example number
  message = "Hi!",
  show = true,
  badgeContent,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleWhatsAppClick = () => {
    // Format WhatsApp link
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Open in new tab
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{
            duration: 0.3,
            type: "spring",
            stiffness: 260,
            damping: 20,
          }}
          style={{
            position: "fixed",
            bottom: isMobile ? 16 : 24,
            left: isMobile ? 16 : 24,
            zIndex: 1100,
          }}
        >
          <Tooltip
            title="Chat with us on WhatsApp"
            placement="right"
            arrow
            open={isHovered}
          >
            <Badge
              badgeContent={badgeContent}
              color="error"
              overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  minWidth: 20,
                  height: 20,
                },
              }}
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onHoverStart={() => setIsHovered(true)}
                onHoverEnd={() => setIsHovered(false)}
              >
                <Fab
                  color="primary"
                  aria-label="chat on whatsapp"
                  onClick={handleWhatsAppClick}
                  sx={{
                    width: isMobile ? 56 : 64,
                    height: isMobile ? 56 : 64,
                    background: "#25D366", // WhatsApp green
                    color: "white",
                    boxShadow: "0 8px 24px rgba(37, 211, 102, 0.4)",
                    "&:hover": {
                      background: "#20BA5A", // Darker green on hover
                      boxShadow: "0 12px 32px rgba(37, 211, 102, 0.6)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <WhatsApp
                    sx={{
                      fontSize: isMobile ? 28 : 32,
                    }}
                  />
                </Fab>
              </motion.div>
            </Badge>
          </Tooltip>

          {/* Pulse animation ring */}
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.7, 0, 0.7],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: "50%",
              border: "2px solid #25D366",
              pointerEvents: "none",
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppFab;
